import React, { useEffect, useState } from "react"
import { toast } from "react-toastify"
import Dropdown from "../commons/Dropdown"
import { getAllCategories, getCategoryById } from "../../firebase/categories"
import { resetShopifyProductCategory, updateShopifyProductCategory } from "../../firebase/shopify"

export default function DialogEditShopifyCategory({ open, item, onClose, onSaved }) {
    const [categories, setCategories] = useState([])
    const [subCategories, setSubCategories] = useState([])
    const [category, setCategory] = useState("")
    const [subCategory, setSubCategory] = useState("")
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (!open) return

        getAllCategories().then((cats) => {
            setCategories(cats.map((cat) => ({ value: cat.id, label: cat.name.toUpperCase() })))
        })

        // A listing not yet assigned a real category carries the literal
        // string 'shopify' here -- that isn't a category doc id, so start
        // the dropdown unselected instead of on a value that doesn't exist.
        const initialCategory = item?.category && item.category !== 'shopify' ? item.category : ""
        setCategory(initialCategory)
        setSubCategory(item?.sub_category && item.sub_category !== 'shopify' ? item.sub_category : "")
    }, [open, item])

    useEffect(() => {
        if (!category) {
            setSubCategories([])
            return
        }

        getCategoryById(category).then((data) => {
            if (data && data.subCategories) {
                setSubCategories(data.subCategories.map((cat) => ({ value: cat, label: cat.toUpperCase() })))
            } else {
                setSubCategories([])
            }
        })
    }, [category])

    // Only a hand-picked category has anything to reset; a mapped one already
    // follows the mapping.
    const handleReset = async () => {
        setSaving(true)
        try {
            await resetShopifyProductCategory(item.id)
            toast.success('Listing now follows your category mapping.')
            onSaved?.()
        } catch (error) {
            console.error('Failed to reset Shopify listing category:', error)
            toast.error(error.message || 'Failed to reset category')
        } finally {
            setSaving(false)
        }
    }

    const handleSave = async () => {
        if (!category) {
            toast.error('Select a category first.')
            return
        }

        setSaving(true)
        try {
            await updateShopifyProductCategory(item.id, category, subCategory)
            toast.success('Category updated.')
            // Handed back so the listings table can patch this one row
            // instead of refetching and losing the seller's place in a long
            // list. A reset calls onSaved with nothing, which the page reads
            // as "reload".
            onSaved?.({ category, subCategory })
        } catch (error) {
            console.error('Failed to update Shopify listing category:', error)
            toast.error(error.message || 'Failed to update category')
        } finally {
            setSaving(false)
        }
    }

    if (!open) return null

    return (
        <div className="fixed inset-0 z-[10000] w-screen overflow-y-auto">
            {/* No backdrop-blur here: it's a known Chromium GPU-compositing
                conflict with native <select> dropdowns -- with the blur on,
                clicking an option can silently fail to register while every
                other click on the dialog still works fine. */}
            <div
                className="fixed inset-0 bg-[#000] bg-opacity-75"
                onClick={onClose}
            />

            <div className="relative flex min-h-full items-center justify-center p-4">
                <div className="relative w-full max-w-[480px] border-[1px] border-gray bg-[#141414] p-6">
                    <button onClick={onClose} className="absolute right-4 top-4">
                        <img src="/assets/icons/icon-close-white.svg" alt="close" />
                    </button>

                    <h2 className="text-white text-xl mb-1">Edit Category</h2>
                    <p className="text-sm text-[#717171] mb-6 uppercase">{item?.title}</p>

                    <div className="flex flex-col gap-5">
                        <label className="w-full text-white">
                            <span className="block mb-2 text-sm">Category</span>
                            <div className="border-[1px] border-gray">
                                <Dropdown
                                    options={categories}
                                    value={category}
                                    placeholder="Select Category"
                                    onChange={(e) => setCategory(e.target.value)}
                                />
                            </div>
                        </label>

                        <label className="w-full text-white">
                            <span className="block mb-2 text-sm">Sub-Category</span>
                            <div className="border-[1px] border-gray">
                                <Dropdown
                                    options={subCategories}
                                    value={subCategory}
                                    placeholder="Select Sub-Category"
                                    onChange={(e) => setSubCategory(e.target.value)}
                                />
                            </div>
                        </label>
                    </div>

                    <button
                        className="solo-button w-full mt-8 text-white border-white border-[1px] bg-primary h-[45px] disabled:opacity-60"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </button>

                    {item?.category_source === 'manual' && (
                        <button
                            className="solo-button w-full mt-3 text-white border-gray border-[1px] h-[45px] disabled:opacity-60"
                            onClick={handleReset}
                            disabled={saving}
                        >
                            Use Category Mapping
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}
